import { type CountryCode, type PaymentMethod } from '@constants'
import { type ReceiptType, type ECommercePaymentTransactionsStatus } from './eCommercePayments'

export enum CampaignStates {
  /** 未開始 */
  NOT_YET = 'not_yet',
  /** 測試中 */
  TESTING = 'testing',
  /** 進行中 */
  IN_PROGRESS = 'in_progress',
  /** 暫停中 */
  PAUSED = 'paused',
  /** 已結束 */
  EXPIRED = 'expired',
}

export interface Brand {
  brand_code: string
  brand_image: string
  brand_name: string
  brand_url: string
}

interface FortuneCampaignOrderSummary {
  order_sales: number
  order_count: number
}

interface FortuneCampaignPayment {
  payment_method: PaymentMethod
  payment_setting: {
    priority: number
  }
}

export enum MemberLogins {
  GUEST = 'guest',
  SUGGEST = 'suggest',
  REQUIRED = 'required',
}

export enum FortuneCampaignSales {
  B2B = 'B2B',
  B2C = 'B2C',
}

export enum FortuneCampaignRegion {
  TW = 'TW',
  HK = 'HK',
  SG = 'SG',
  US = 'US',
}

export interface Staff {
  phone: string
}

export interface FortuneCampaign {
  id: number
  sales_type: FortuneCampaignSales
  brands: Brand[]
  code: string
  enabled?: boolean
  status: CampaignStates
  name: string
  start_time: string
  end_time: string
  region: CountryCode
  currency: string
  campaign_order_summary?: FortuneCampaignOrderSummary
  campaign_payments: FortuneCampaignPayment[]
  invoice_platform?: string
  member_login_type: MemberLogins
  enable_giveaway?: boolean
  enable_pickup_giveaway?: boolean
  enable_delivery_giveaway?: boolean
  has_products: boolean
  timezone_offset: string
  admin_users: Staff[]
  tax_rate?: number
}

export interface FortuneCampaignSettingsOptions {
  currency: string
  brand_max_count: number
  payment_methods: PaymentMethod[]
  timezone_offset: string
  invoice_platforms: string[]
  member_login_types: string[]
}

export enum FastpassOrderStatus {
  /**
   * 尚未成立
   */
  UNCREATED = 'uncreated',
  /**
   * 尚未付款
   */
  PENDING = 'pending',
  /**
   * 處理中
   */
  PROCESSING = 'processing',
  /**
   * 已完成
   */
  COMPLETED = 'completed',
  /**
   * 已取消
   */
  CANCELLED = 'cancelled',
}

export interface FpPaymentTransactions {
  id: string
  status: ECommercePaymentTransactionsStatus
  total: number
  subtotal: number
  tax: number
  payment_method: PaymentMethod
  /** 付款資訊(目前只有ecpay_atm會有) */
  payment_info: {
    bank_code: string
    account: string
    expire_date: string
    merchant_trade_no: number
    trade_no: number
    trade_date: string
  }
  /** 滿額禮是否要宅配寄送 */
  is_giveaway_dispatch: boolean
  discount_total: number
  shipping_total: number
  /** 滿額禮是否已確認 */
  item_confirmed: boolean
  country_code: string
  customer_note: string
  order_hash: string
  payment_secret: string
  delivery_date: string
  delivery_time_slot: string
  purchase_order_name: string
  receiver_name: string
  receiver_email: string
  receiver_phone: string
  shipping_methods: string[]
  shipping_address: string
  /** 是否需要建立訂單明細PDF */
  need_pdf: boolean
  posting_date_in_next_month: boolean
  refund_statement: boolean
  receipt_type: ReceiptType
  created_at: string
  invoice_option: {
    business_ubn_title: string
    business_ubn: string
    phone_carrier_number: string
    charity_organization: string
  }
  child_orders: {
    id: string
    order_id: string
    project_code: string
    country_code: string
    status: string
    customer_id: number
    customer_note: string
    delivery_reason: string
    shipping_method: string
    shipping_method_title: string
    total: number
    shipping: ShippingInfo
    discount_total: number
    shipping_total: number
    order_items: OrderItem[]
  }[]
  payment_redirect_url: string | null
}

interface ShippingInfo {
  id: number
  order_id: number
  sequence_index: number
  status: string
  shipping_date: string
  billing_address_email: string
  billing_address_phone: string
  billing_address_company: string
  billing_address_postcode: string
  billing_address_country: string
  billing_address_state: string
  billing_address_city: string
  billing_address_meta: AddressMeta
  shipping_address_company: string
  shipping_address_postcode: string
  shipping_address_country: string
  shipping_address_state: string
  shipping_address_city: string
  shipping_address_meta: AddressMeta
  created_at: string
  updated_at: string
  deleted_at: string
}

interface AddressMeta {
  last_name: string
  first_name: string
  address1: string
  address2: string
}

interface OrderItem {
  project_code: string
  id: string
  product_id: number
  variation_id: number
  sku: string
  stock: number
  original_price: number
  sale_price: number
  attributes: Record<string, string>
  weight_attribute: string
  is_out_of_stock: boolean
  delivery_class: string
  bundle_id: string
  is_addon: boolean
  limited_quantity: number
  quantity: number
  available_campaign_bundles: {
    campaign_bundle_id: number
    display_name: string
    is_applied: boolean
  }[]
  payment_redirect_url: string | null
}

export interface ShippingUploadRecord {
  id: number
  user_id: number
  project_code: string | null
  campaign_id: number | null
  file_path: string
  file_name: string
  message: string
  created_at: string
  updated_at: string
}
